import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Drawer } from "antd";
import { message } from "../../components/antdMessageBridge";
import { CloseCircleOutlined } from "@ant-design/icons";
import "../manifest_verificationqueue/verification-slider.css";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import CommonObj from "../../CommonObj";
import { useAuth } from "../../auth/AuthContext";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";

const QCSliderUI = (props) => {
	const [iform, setIForm] = useState({
		displayname: "",
		status: "",
	});
	const { roleUserId } = useAuth();
	const { accessToken } = useAuthToken();
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [ierrors, setIErrors] = useState({});

	const [selectedStatus, setSelectedStatus] = useState();
	const [error, setError] = useState(null);

	const fetchUserData = async (categoryId) => {
		setLoading(true);
		const requestData = {
			CategoryId: categoryId,
			UserId: roleUserId,
		};

		let formData = new FormData();
		Object.keys(requestData).forEach(function (key) {
			formData.append(key, requestData[key]);
		});

		try {
			const myHeaders = {
				Authorization: `Bearer ${accessToken}`,
				"X-CSRF-Token": encryptTimestamp(),
			};
			const response = await fetch(
				CommonObj.Endpoints.GetQCErrorCategoryById,
				{
					method: "POST",
					headers: myHeaders,
					body: formData,
				}
			);

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}
			const responseText = await response.text();
			let result;
			result = JSON.parse(responseText);
			setIForm({
				displayname: result.category || "",
				status: result.status || "",
			});
			setSelectedStatus(result.status);
		} catch (err) {
			setError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!props.selectedRecord && props.visible) {
			setIForm({
				displayname: "",
				status: "",
			});
			setSelectedStatus(true);
		}
	}, [props.visible, props.selectedRecord]);

	useEffect(() => {
		if (props.selectedRecord && props.visible) {
			setIErrors({});
			fetchUserData(props?.selectedRecord?.categoryId);
		}
	}, [props.selectedRecord, props.visible]);

	const handleInternalSubmit = async (e) => {
		e.preventDefault();

		if (!i_validate()) {
			focusFirstError(ierrors);
			return;
		}

		setLoading(true);
		try {
			const isEdit = !!props.selectedRecord;
			const formData = getRequestFormData(isEdit);
			const endpoint = isEdit
				? CommonObj.Endpoints.UpdateQCErrorCategory
				: CommonObj.Endpoints.CreateQCErrorCategory;

			const response = await fetch(endpoint, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					"X-CSRF-Token": encryptTimestamp(),
				},
				body: formData,
			});

			if (!response.ok) throw new Error(`Status: ${response.status}`);

			const result = await response.json();
			processApiResponse(result, isEdit);
		} catch (error) {
			message.error(`Error submitting form: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const focusFirstError = (errors) => {
		const firstField = Object.keys(errors)[0];
		const element = document.getElementById(firstField);
		if (element) element.focus();
	};

	const getRequestFormData = (isEdit) => {
		const formData = new FormData();
		const payload = {
			Category: iform.displayname,
			UserId: roleUserId,
			...(isEdit && {
				CategoryId: props.selectedRecord.categoryId,
				Status: selectedStatus ? 1 : 0,
			}),
		};
		Object.entries(payload).forEach(([k, v]) => formData.append(k, v));
		return formData;
	};

	const processApiResponse = (result, isEdit) => {
		if (result?.id === "0") {
			return message.error("Error Category already exists.");
		}

		if (result) {
			message.success(
				`Error Category ${isEdit ? "updated" : "created"} successfully.`
			);
			handleClear();
			props.onClose();
			props.onSuccess?.();
			return;
		}

		message.error(result?.message || "Failed to save the data");
	};

	const handleClear = () => {
		setSelectedStatus(true);
		setSelectedStatus(false);
		setIForm({
			displayname: "",
		});
		setErrors({});
		setIErrors({});
	};

	const i_validate = () => {
		const newErrors = {};

		if (!iform.displayname?.trim()) {
			newErrors.displayname = "Please enter Error Category";
		}

		setIErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInternalChange = (e) => {
		const { name, value } = e.target;
		setIForm((prev) => ({ ...prev, [name]: value }));
		if (ierrors[name]) {
			setIErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	const handlestatusChange = (e) => {
		setSelectedStatus(e.target.checked);
	};

	useEffect(() => {
		if (props.selectedRecord) {
			message.info(props.selectedRecord);
		}
		setIForm({
			displayname: "",
			status: "",
		});
	}, []);

	const handleClose = () => {
		setIErrors({});
		props.onClose();
	};

	return (
		<Drawer
			title={props.selectedRecord ? "Edit Error Category" : props.title}
			placement={props.placement}
			onClose={handleClose}
			open={props.visible}
			width={props.width}
			className={props.className}
			closeIcon={<CloseCircleOutlined />}
			loading={loading}
		>
			<div className="modal-body">
				<div className="">
					<form
						className="book-page-form"
						onSubmit={handleInternalSubmit}
						noValidate
					>
						<div className="add-user-internal-container">
							<div className="field-group">
								<input
									id="displayname"
									name="displayname"
									className={`${"book-page-form-input-text"} ${iform?.displayname === "" ? "" : "has-text"
										}`}
									maxLength={100}
									value={iform.displayname || ""}
									onChange={(e) => {
										const value = e.target.value;
										handleInternalChange({
											target: { name: "displayname", value: value },
										});
									}}
									required
								/>
								<label htmlFor="displayname" className="label">
									Error Category <span className="form-required">*</span>
								</label>
								{ierrors.displayname && (
									<span className="error-msg">{ierrors.displayname}</span>
								)}
							</div>
							{props?.selectedRecord && (
								<div className="book-page-form-checkbox-button">
									<input
										type="checkbox"
										id="status"
										name="status"
										data-track="qc-error-master-edit-is-active-toggle"
										onChange={handlestatusChange}
										checked={selectedStatus || undefined}
									/>
									<label htmlFor="status">Is Active</label>
									<span className="error-msg">{ierrors.status}</span>
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button className="modal-cancel-button" onClick={handleClear}>
								Clear
							</button>
							<button className="accept-button" data-track="qc-error-master-submit-category">
								Submit
							</button>
						</div>
					</form>
				</div>
			</div>
		</Drawer>
	);
};

QCSliderUI.propTypes = {
	visible: PropTypes.bool.isRequired,
	title: PropTypes.string,
	placement: PropTypes.oneOf(["right", "left", "top", "bottom"]),
	width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	className: PropTypes.string,
	selectedRecord: PropTypes.shape({
		categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
			.isRequired,
	}),
	onClose: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
};

QCSliderUI.defaultProps = {
	title: "Add Error Category",
	placement: "right",
	width: 400,
	className: "",
	selectedRecord: null,
	onSuccess: undefined,
};

export default QCSliderUI;